import { useTranslation } from 'react-i18next';

import { Button, Flex, Text } from '@bonusly/design-system';

interface TruncationCaptionProps {
	/** Number of rows / groups actually shown. */
	shown: number;
	/** Full count when trimmed (M in "N of M"). */
	total: number;
	/** Ranking direction, when one applies: "top" (highest) / "bottom" (lowest). */
	selection?: 'top' | 'bottom';
	/** When provided, renders a "Show all" affordance that re-requests the full set. */
	onExpand?: () => void;
}

/**
 * The "N of M" truncation disclosure shared by the chart, matrix, and table
 * evidence nodes. Renders nothing when not truncated.
 */
export function TruncationCaption({ shown, total, selection, onExpand }: TruncationCaptionProps) {
	const { t } = useTranslation('admin_bizy');
	if (!(total > shown)) return null;

	const { key, fallback } =
		selection === 'top'
			? { key: 'truncation.top', fallback: 'Showing the top {{shown}} of {{total}}.' }
			: selection === 'bottom'
				? { key: 'truncation.bottom', fallback: 'Showing the bottom {{shown}} of {{total}}.' }
				: { key: 'truncation.neutral', fallback: 'Showing {{shown}} of {{total}}.' };

	return (
		<Flex align="baseline" gap={1} flexWrap="wrap">
			<Text fontSize="xs" color="text.muted">
				{t(key, { shown, total, defaultValue: fallback })}
			</Text>
			{onExpand ? (
				<Button variant="link" size="xs" onClick={onExpand}>
					{t('truncation.showAll')}
				</Button>
			) : null}
		</Flex>
	);
}
